import dayjs from "dayjs";
import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { Image, Present, Banner, Carousel, Reshare, DailyRanking, Month, Response, Heart } from "@similarweb/icons";
import { shortMonthAndDay, fullMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { InfoItem, type InfoItemProps } from "../../../common/InfoItem";
import { OpenInNewTab } from "../../../common/OpenInNewTab";
import type { ContentType, SocialPostData } from "../types";
import { formatEngagementNumber } from "../utils";

const Wrapper = styled(Flex)`
    justify-content: space-between;
    align-items: center;
    background-color: ${colorsPalettes.carbon[25]};
    padding: 2px 8px;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
`;

const CONTENT_TYPE_CONFIG: Record<ContentType, { icon: InfoItemProps["icon"]; title: string }> = {
    Image: { icon: Image, title: "Image" },
    Video: { icon: Present, title: "Video" },
    Text: { icon: Banner, title: "Text" },
    Carousel: { icon: Carousel, title: "Carousel" },
    Link: { icon: Image, title: "Link" },
    Event: { icon: DailyRanking, title: "Event" },
    Reshare: { icon: Reshare, title: "Reshare" },
};

interface InfoFooterProps {
    type: ContentType;
    info: Pick<SocialPostData, "reactions" | "comments" | "shares" | "views" | "publishedDate">;
    postUrl?: string;
}

export const InfoFooter = ({ type, info, postUrl }: InfoFooterProps) => {
    const { translate } = useTranslate();
    const { reactions, comments, publishedDate } = info;
    const { icon: ContentIcon, title: contentTitle } = CONTENT_TYPE_CONFIG[type];
    const formattedReactions = formatEngagementNumber(reactions ?? 0);
    const formattedComments = formatEngagementNumber(comments ?? 0);
    const date = dayjs(publishedDate);

    return (
        <Wrapper data-automation="info-footer">
            <Flex gap="12px">
                <InfoItem
                    icon={ContentIcon}
                    title={contentTitle}
                    tooltip={translate("activation.alerts.cards.social-post.creative-type", { type: contentTitle })}
                />
                <InfoItem
                    icon={Month}
                    title={date.format(shortMonthAndDay)}
                    tooltip={translate("activation.alerts.cards.social-post.first-seen", {
                        date: date.format(fullMonthDayAndYear),
                    })}
                />
                <InfoItem
                    icon={Heart}
                    title={formattedReactions}
                    tooltip={translate("activation.alerts.cards.social-post.reactions", {
                        reactions: formattedReactions,
                    })}
                />
                <InfoItem
                    icon={Response}
                    title={formattedComments}
                    tooltip={translate("activation.alerts.cards.social-post.comments", { comments: formattedComments })}
                />
            </Flex>
            <Tooltip content={translate("activation.alerts.cards.social-post.view-post")}>
                <OpenInNewTab
                    url={postUrl}
                    data-automation="open-post-button"
                />
            </Tooltip>
        </Wrapper>
    );
};
